import { installAttributionKinds } from "@redotech/account-common/install-attribution";
import { InstallSource } from "@redotech/account-common/install-source";
import { ReclaimCampaignMatchKind as ReclaimCampaignMatchKindEnum } from "@redotech/crdb-enums/disputes/reclaim-campaign-match-kind";
import * as z from "zod";

export const reclaimAccessModes = ["trial", "free"] as const;
export type ReclaimAccessMode = (typeof reclaimAccessModes)[number];

export const disputesAccessOverrides = [
  "default",
  "trial",
  "free",
  "paid",
] as const;
export type DisputesAccessOverride = (typeof disputesAccessOverrides)[number];
export const DisputesAccessOverride = disputesAccessOverrides;

export const reclaimAccessSources = [
  "intake_modal",
  "placeholder_landing",
  "shopify_reclaim_install",
] as const;
export type ReclaimAccessSource = (typeof reclaimAccessSources)[number];
/**
 * Runtime companion for the type above. The Mongoose enum-drift checker
 * resolves the declared identifier with `Reflect.get` on this module's exports,
 * so a bare type alias resolves to undefined and fails the invariant. Every
 * other typedUnion source pairs the type with a same-named const this way.
 */
export const ReclaimAccessSource = reclaimAccessSources;

/**
 * Surfaces a merchant can activate from that the team record cannot reveal.
 * Only the in-dashboard Reclaim page needs one; everything else is derived.
 */
export const reclaimActivationSurfaces = ["in_app_discovery"] as const;
export type ReclaimActivationSurface =
  (typeof reclaimActivationSurfaces)[number];

/** Team facts the channel is derived from; never a URL parameter. */
export interface ReclaimChannelInputs {
  installSource?: string | null;
  /** Set by the Reclaim Shopify app install, on teams of any install source. */
  reclaimShopifyAppInstalledAt?: Date | null;
  /** Admin escape hatch, ahead of everything derived. */
  channelOverride?: ReclaimAccessSource | null;
  surface?: ReclaimActivationSurface;
}

/**
 * Which commercial channel a merchant belongs to. Derived from what the team
 * already durably records, so a stale or absent URL parameter cannot decide
 * money, and a merchant cannot self-serve into cheaper terms.
 */
export function resolveReclaimChannel({
  installSource,
  reclaimShopifyAppInstalledAt,
  channelOverride,
  surface,
}: ReclaimChannelInputs): ReclaimAccessSource {
  if (channelOverride != null && reclaimAccessSourceSet.has(channelOverride)) {
    return channelOverride;
  }
  // A Reclaim-app install does not rewrite an existing team's installSource,
  // so the timestamp is the only signal for a merchant who added it later.
  if (
    reclaimShopifyAppInstalledAt != null ||
    installSource === InstallSource.RECLAIM_APP
  ) {
    return "shopify_reclaim_install";
  }
  if (surface === "in_app_discovery") {
    return "placeholder_landing";
  }
  return "intake_modal";
}

const reclaimAccessSourceSet: ReadonlySet<string> = new Set(
  reclaimAccessSources,
);

export const reclaimSourceRulePricingModes = ["default", "custom"] as const;
export type ReclaimSourceRulePricingMode =
  (typeof reclaimSourceRulePricingModes)[number];

/** Off the generated CRDB enum; widened so callers can pass plain strings. */
const reclaimCampaignMatchKindSchema_ = z.enum(ReclaimCampaignMatchKindEnum);
export type ReclaimCampaignMatchKind = `${ReclaimCampaignMatchKindEnum}`;
export const reclaimCampaignMatchKindSchema: z.ZodType<ReclaimCampaignMatchKind> =
  reclaimCampaignMatchKindSchema_;

/**
 * Terms for one marketing campaign. A "default" access or pricing mode means no
 * opinion: that dimension falls through to the surface rule.
 */
export interface ReclaimCampaignRule {
  matchKind: ReclaimCampaignMatchKind;
  matchValue: string;
  label: string;
  accessMode: DisputesAccessOverride;
  pricingMode: ReclaimSourceRulePricingMode;
  successFeePercentage?: number;
  trialMonths?: number;
  /** Optional window; outside it the campaign matches nothing. */
  startsAt?: Date | null;
  endsAt?: Date | null;
  enabled: boolean;
}

const reclaimCampaignRuleSchema_ = z
  .object({
    matchKind: reclaimCampaignMatchKindSchema,
    matchValue: z.string().min(1).max(200),
    label: z.string().min(1),
    accessMode: z.enum(disputesAccessOverrides),
    pricingMode: z.enum(reclaimSourceRulePricingModes),
    successFeePercentage: z.number().min(0).max(100).optional(),
    trialMonths: z.number().int().min(1).max(120).optional(),
    startsAt: z.date().nullable().optional(),
    endsAt: z.date().nullable().optional(),
    enabled: z.boolean(),
  })
  .superRefine((rule, ctx) => {
    if (rule.startsAt && rule.endsAt && rule.startsAt >= rule.endsAt) {
      ctx.addIssue({
        code: "custom",
        path: ["endsAt"],
        message: "The campaign must end after it starts.",
      });
    }
    if (
      rule.matchKind === "attribution_kind" &&
      !(installAttributionKinds as readonly string[]).includes(
        rule.matchValue.trim().toLowerCase(),
      )
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["matchValue"],
        message: `Ad channel must be one of ${installAttributionKinds.join(", ")}.`,
      });
    }
  });

/** Pinned to the interface so `matchKind` widens off the nominal CRDB enum. */
export const reclaimCampaignRuleSchema: z.ZodType<ReclaimCampaignRule> =
  reclaimCampaignRuleSchema_;

export const RECLAIM_CAMPAIGN_PRICING_SOURCE_PREFIX = "campaign:";

export function reclaimCampaignPricingSource(
  campaign: ReclaimCampaignRule,
): string {
  return `${RECLAIM_CAMPAIGN_PRICING_SOURCE_PREFIX}${campaign.matchKind}:${campaign.matchValue}`;
}

export interface ReclaimCampaignCandidate {
  /** Merchant-supplied link code. A lookup key only, never terms. */
  linkCode?: string | null;
  utmCampaign?: string | null;
  /** Derived channels, e.g. "meta_ad" — see deriveInstallAttribution. */
  attributionKinds?: readonly string[];
}

function campaignMatches(
  campaign: ReclaimCampaignRule,
  values: readonly (string | null | undefined)[],
): boolean {
  const target = campaign.matchValue.trim().toLowerCase();
  return values.some(
    (value) => value != null && value.trim().toLowerCase() === target,
  );
}

/**
 * Does this link code name a campaign at all, regardless of window or enabled
 * state? The code is only a lookup key: holding it is safe because activation
 * re-resolves it with the window applied, so an out-of-window code still grants
 * nothing. Holding it is what lets a merchant who clicks early and finishes
 * inside the window get the terms they were sent.
 */
export function findReclaimCampaignByLinkCode(
  campaigns: readonly ReclaimCampaignRule[],
  linkCode: string,
): ReclaimCampaignRule | undefined {
  return campaigns.find(
    (campaign) =>
      campaign.matchKind === "link_code" &&
      campaignMatches(campaign, [linkCode]),
  );
}

/** Live now: switched on and inside its window, if it has one. */
export function isReclaimCampaignActive(
  campaign: ReclaimCampaignRule,
  now: Date,
): boolean {
  return (
    campaign.enabled &&
    (campaign.startsAt == null || campaign.startsAt <= now) &&
    (campaign.endsAt == null || now < campaign.endsAt)
  );
}

/**
 * Most specific identifier wins. Anything unmatched returns undefined so the
 * caller falls through to the surface rule — never to free.
 */
export function matchReclaimCampaign(
  campaigns: readonly ReclaimCampaignRule[],
  candidate: ReclaimCampaignCandidate,
  now: Date,
): ReclaimCampaignRule | undefined {
  const enabled = campaigns.filter((campaign) =>
    isReclaimCampaignActive(campaign, now),
  );
  const byKind: [
    ReclaimCampaignMatchKind,
    readonly (string | null | undefined)[],
  ][] = [
    ["link_code", [candidate.linkCode]],
    ["utm_campaign", [candidate.utmCampaign]],
    ["attribution_kind", candidate.attributionKinds ?? []],
  ];
  for (const [matchKind, values] of byKind) {
    const found = enabled.find(
      (campaign) =>
        campaign.matchKind === matchKind && campaignMatches(campaign, values),
    );
    if (found) {
      return found;
    }
  }
  return undefined;
}

export interface ReclaimPricingTerms {
  successFeePercentage: number;
  minFeeDollars: number;
}

export interface ReclaimSourceRule {
  accessMode: DisputesAccessOverride;
  pricingMode: ReclaimSourceRulePricingMode;
  successFeePercentage?: number;
  minFeeDollars?: number;
}

export type ReclaimSourceRules = Record<ReclaimAccessSource, ReclaimSourceRule>;

export interface ReclaimAccessDefaults {
  accessMode: ReclaimAccessMode;
  trialMonths: number;
  successFeePercentage: number;
  minFeeDollars: number;
  sourceRules: ReclaimSourceRules;
}

export interface ReclaimResolvedTerms extends ReclaimPricingTerms {
  accessMode: ReclaimAccessMode | "paid";
  trialMonths: number;
}

export interface ReclaimPricingOverride extends ReclaimPricingTerms {
  source?: string;
  appliedAt?: Date;
}

export interface ReclaimSourceMaterialization {
  accessOverride: DisputesAccessOverride;
  pricingOverride: ReclaimPricingOverride;
  /** Trial length the resolved terms call for; absent when no trial can follow. */
  trialMonths?: number;
}

export const RECLAIM_PARTNER_REFERRAL_PRICING_SOURCE_PREFIX =
  "partner_referral:";

export interface ReclaimCommercialTermsDisputes {
  accessOverride?: DisputesAccessOverride | null;
  freeAccessEnabled?: boolean | null;
  pricingOverride?: {
    successFeePercentage?: number | null;
    minFeeDollars?: number | null;
  } | null;
  billing?: {
    feePercentage?: number | null;
    minFeeDollars?: number | null;
  } | null;
  trialMonths?: number | null;
}

export const DEFAULT_RECLAIM_TRIAL_MONTHS = 6;
export const DEFAULT_RECLAIM_SUCCESS_FEE_PERCENTAGE = 25;
export const DEFAULT_RECLAIM_MIN_FEE_DOLLARS = 0;

export const DEFAULT_RECLAIM_SOURCE_RULES: ReclaimSourceRules = {
  // A fresh install on either app is an acquisition worth buying, so it is free.
  // In-app discovery is an existing Redo merchant finding Reclaim from inside a
  // dashboard they already pay for, so it is billed.
  intake_modal: { accessMode: "free", pricingMode: "default" },
  placeholder_landing: { accessMode: "paid", pricingMode: "default" },
  shopify_reclaim_install: { accessMode: "free", pricingMode: "default" },
};

export const DEFAULT_RECLAIM_ACCESS_DEFAULTS: ReclaimAccessDefaults = {
  accessMode: "trial",
  trialMonths: DEFAULT_RECLAIM_TRIAL_MONTHS,
  successFeePercentage: DEFAULT_RECLAIM_SUCCESS_FEE_PERCENTAGE,
  minFeeDollars: DEFAULT_RECLAIM_MIN_FEE_DOLLARS,
  sourceRules: DEFAULT_RECLAIM_SOURCE_RULES,
};

export const reclaimPricingTermsSchema = z.object({
  successFeePercentage: z.number().min(0).max(100),
  minFeeDollars: z.number().min(0),
});

export const reclaimSourceRuleSchema = z.object({
  accessMode: z.enum(disputesAccessOverrides),
  pricingMode: z.enum(reclaimSourceRulePricingModes),
  successFeePercentage: z.number().min(0).max(100).optional(),
  minFeeDollars: z.number().min(0).optional(),
});

/** `satisfies` keeps this in lockstep with {@link reclaimAccessSources}: a
 * missing key is a compile error, not a key silently stripped over the wire. */
const reclaimSourceRulesShape = {
  intake_modal: reclaimSourceRuleSchema,
  placeholder_landing: reclaimSourceRuleSchema,
  shopify_reclaim_install: reclaimSourceRuleSchema,
} satisfies Record<ReclaimAccessSource, typeof reclaimSourceRuleSchema>;

export const reclaimSourceRulesSchema = z.object(reclaimSourceRulesShape);

export const reclaimAccessDefaultsSchema = z.object({
  accessMode: z.enum(reclaimAccessModes),
  trialMonths: z.number().int().min(1).max(120),
  successFeePercentage: z.number().min(0).max(100),
  minFeeDollars: z.number().min(0),
  sourceRules: reclaimSourceRulesSchema,
});

export function normalizeReclaimAccessDefaults(
  input: unknown,
): ReclaimAccessDefaults {
  const overrides =
    input && typeof input === "object" && !Array.isArray(input)
      ? Object.fromEntries(Object.entries(input))
      : {};
  const sourceRulesInput: Record<string, unknown> =
    overrides.sourceRules &&
    typeof overrides.sourceRules === "object" &&
    !Array.isArray(overrides.sourceRules)
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        (overrides.sourceRules as Record<string, unknown>)
      : {};
  const successFeePercentage =
    typeof overrides.successFeePercentage === "number"
      ? overrides.successFeePercentage
      : DEFAULT_RECLAIM_ACCESS_DEFAULTS.successFeePercentage;
  const minFeeDollars =
    typeof overrides.minFeeDollars === "number"
      ? overrides.minFeeDollars
      : DEFAULT_RECLAIM_ACCESS_DEFAULTS.minFeeDollars;
  const mergedDefaults = {
    ...DEFAULT_RECLAIM_ACCESS_DEFAULTS,
    ...overrides,
    sourceRules: reclaimAccessSources.reduce<Partial<ReclaimSourceRules>>(
      (sourceRules, source) => {
        const rawSourceRule = sourceRulesInput[source];
        const sourceRuleInput =
          rawSourceRule &&
          typeof rawSourceRule === "object" &&
          !Array.isArray(rawSourceRule)
            ? Object.fromEntries(Object.entries(rawSourceRule))
            : {};
        const rule = {
          ...DEFAULT_RECLAIM_SOURCE_RULES[source],
          ...sourceRuleInput,
        };
        sourceRules[source] =
          rule.pricingMode === "custom"
            ? {
                ...rule,
                successFeePercentage:
                  rule.successFeePercentage ?? successFeePercentage,
                minFeeDollars: rule.minFeeDollars ?? minFeeDollars,
              }
            : { accessMode: rule.accessMode, pricingMode: "default" };
        return sourceRules;
      },
      {},
    ),
  };
  return reclaimAccessDefaultsSchema.parse(mergedDefaults);
}

export function resolveReclaimSourceRule(
  accessDefaults: ReclaimAccessDefaults,
  source: ReclaimAccessSource,
): ReclaimSourceRule {
  return (
    accessDefaults.sourceRules?.[source] ?? DEFAULT_RECLAIM_SOURCE_RULES[source]
  );
}

/** A campaign expressed as a source rule, so one code path resolves both. */
function campaignAsSourceRule(
  campaign: ReclaimCampaignRule,
): ReclaimSourceRule {
  const rule: ReclaimSourceRule = {
    accessMode: campaign.accessMode,
    pricingMode: campaign.pricingMode,
  };
  if (campaign.successFeePercentage != null) {
    rule.successFeePercentage = campaign.successFeePercentage;
  }
  return rule;
}

/**
 * A campaign outranks the surface it arrived on, per dimension — a campaign that
 * sets only pricing still takes its access mode from the surface rule.
 */
export function resolveReclaimTerms(
  accessDefaults: ReclaimAccessDefaults,
  source?: ReclaimAccessSource,
  campaign?: ReclaimCampaignRule,
): ReclaimResolvedTerms {
  const activeCampaign = campaign?.enabled === true ? campaign : undefined;
  const sourceRule = source
    ? resolveReclaimSourceRule(accessDefaults, source)
    : undefined;
  const campaignRule = activeCampaign
    ? campaignAsSourceRule(activeCampaign)
    : undefined;
  const accessRule =
    campaignRule && campaignRule.accessMode !== "default"
      ? campaignRule
      : sourceRule;
  const pricingRule =
    campaignRule?.pricingMode === "custom" ? campaignRule : sourceRule;
  const accessMode =
    accessRule && accessRule.accessMode !== "default"
      ? accessRule.accessMode
      : accessDefaults.accessMode;
  return {
    accessMode,
    trialMonths: activeCampaign?.trialMonths ?? accessDefaults.trialMonths,
    ...resolveReclaimPricingTerms(accessDefaults, pricingRule),
  };
}

export function resolveReclaimCommercialTerms({
  accessDefaults,
  disputes,
  source,
  campaign,
}: {
  accessDefaults: ReclaimAccessDefaults;
  disputes?: ReclaimCommercialTermsDisputes | null;
  source?: ReclaimAccessSource;
  campaign?: ReclaimCampaignRule;
}): ReclaimResolvedTerms {
  const sourceTerms = resolveReclaimTerms(accessDefaults, source, campaign);
  const trialMonths =
    disputes?.trialMonths != null && disputes.trialMonths >= 1
      ? disputes.trialMonths
      : sourceTerms.trialMonths;

  return {
    accessMode: resolveReclaimCommercialAccessMode(disputes, sourceTerms),
    trialMonths,
    ...resolveReclaimCommercialPricing(disputes, sourceTerms),
  };
}

export function resolveReclaimPricingTerms(
  accessDefaults: ReclaimAccessDefaults,
  sourceRule?: ReclaimSourceRule,
): ReclaimPricingTerms {
  if (sourceRule?.pricingMode === "custom") {
    return reclaimPricingTermsSchema.parse({
      successFeePercentage:
        sourceRule.successFeePercentage ?? accessDefaults.successFeePercentage,
      minFeeDollars: sourceRule.minFeeDollars ?? accessDefaults.minFeeDollars,
    });
  }
  return {
    successFeePercentage: accessDefaults.successFeePercentage,
    minFeeDollars: accessDefaults.minFeeDollars,
  };
}

export function getReclaimSourceMaterialization(
  accessDefaults: ReclaimAccessDefaults,
  source: ReclaimAccessSource,
  appliedAt: Date,
  campaign?: ReclaimCampaignRule,
): ReclaimSourceMaterialization {
  // Always materialize concrete access and pricing values onto the merchant
  // at flow time. When the source rule says "default", resolve through to
  // current globals so the team carries a self-contained, frozen record of
  // the terms in effect at activation. Admin global edits will only affect
  // future activations.
  const activeCampaign = campaign?.enabled === true ? campaign : undefined;
  const terms = resolveReclaimTerms(accessDefaults, source, activeCampaign);

  return {
    accessOverride: terms.accessMode,
    pricingOverride: {
      successFeePercentage: terms.successFeePercentage,
      // Commercial policy: no minimum, ever.
      minFeeDollars: 0,
      // Record the campaign when one applied, so the team carries why.
      source: activeCampaign
        ? reclaimCampaignPricingSource(activeCampaign)
        : source,
      appliedAt,
    },
    trialMonths: terms.trialMonths,
  };
}

export function getReclaimPartnerReferralFreeAccessMaterialization({
  appliedAt,
  referralCode,
}: {
  appliedAt: Date;
  referralCode: string;
}): ReclaimSourceMaterialization {
  const normalizedReferralCode = referralCode.trim().toUpperCase();
  return {
    accessOverride: "free",
    pricingOverride: {
      successFeePercentage: 0,
      minFeeDollars: 0,
      source: `${RECLAIM_PARTNER_REFERRAL_PRICING_SOURCE_PREFIX}${normalizedReferralCode}`,
      appliedAt,
    },
  };
}

function resolveReclaimCommercialAccessMode(
  disputes: ReclaimCommercialTermsDisputes | null | undefined,
  sourceTerms: ReclaimResolvedTerms,
): ReclaimResolvedTerms["accessMode"] {
  if (disputes?.accessOverride === "paid") {
    return "paid";
  }
  if (
    disputes?.accessOverride === "free" ||
    disputes?.freeAccessEnabled === true
  ) {
    return "free";
  }
  if (disputes?.accessOverride === "trial") {
    return "trial";
  }
  return sourceTerms.accessMode;
}

function resolveReclaimCommercialPricing(
  disputes: ReclaimCommercialTermsDisputes | null | undefined,
  sourceTerms: ReclaimResolvedTerms,
): ReclaimPricingTerms {
  // Commercial policy: Reclaim has no dollar minimum, ever. Legacy team docs
  // and source rules may still carry a minFeeDollars value; ignore it here
  // so neither billing math (Math.max(min, ...)) nor pitch copy can apply
  // one.
  if (disputes?.pricingOverride?.successFeePercentage != null) {
    return {
      successFeePercentage: disputes.pricingOverride.successFeePercentage,
      minFeeDollars: 0,
    };
  }

  if (disputes?.billing?.feePercentage != null) {
    return {
      successFeePercentage: disputes.billing.feePercentage,
      minFeeDollars: 0,
    };
  }

  return {
    successFeePercentage: sourceTerms.successFeePercentage,
    minFeeDollars: 0,
  };
}
